import { useEffect, useState } from "react";
import { Globe, Plus, Trash2, X, GripVertical, Eye, EyeOff } from "lucide-react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api } from "@/lib/api";
import { LANGS } from "@/lib/i18n";
import { toast } from "sonner";

export default function StaffContentPage() {
  const [list, setList] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [doc, setDoc] = useState(null);
  const [lang, setLang] = useState("en");
  const [showAdd, setShowAdd] = useState(false);

  function load() {
    api.get("/content-blocks?enabled_only=false").then((r) => {
      setList(r.data);
      if (r.data.length) {
        const found = r.data.find((b) => b.section_key === activeKey) || r.data[0];
        setActiveKey(found.section_key);
        setDoc({ ...found, items: found.items || [] });
      } else {
        setActiveKey(null); setDoc(null);
      }
    });
  }
  useEffect(load, []);

  function pick(k) {
    const b = list.find((x) => x.section_key === k);
    setActiveKey(k);
    setDoc({ ...b, items: b.items || [] });
    setLang("en");
  }

  function setSectionField(field, value) {
    if (lang === "en") {
      setDoc({ ...doc, [field]: value });
    } else {
      const i18n = { ...(doc.i18n || {}) };
      i18n[lang] = { ...(i18n[lang] || {}), [field]: value };
      setDoc({ ...doc, i18n });
    }
  }
  function getSectionField(field) {
    if (lang === "en") return doc?.[field] ?? "";
    return doc?.i18n?.[lang]?.[field] ?? "";
  }

  function setItemField(idx, field, value) {
    const items = [...(doc.items || [])];
    if (lang === "en") {
      items[idx] = { ...items[idx], [field]: value };
    } else {
      const item = { ...items[idx] };
      const i18n = { ...(item.i18n || {}) };
      i18n[lang] = { ...(i18n[lang] || {}), [field]: value };
      item.i18n = i18n;
      items[idx] = item;
    }
    setDoc({ ...doc, items });
  }
  function getItemField(idx, field) {
    const it = doc?.items?.[idx];
    if (!it) return "";
    if (lang === "en") return it?.[field] ?? "";
    return it?.i18n?.[lang]?.[field] ?? "";
  }

  function addItem() {
    setDoc({ ...doc, items: [...(doc.items || []), { title: "", content: "" }] });
  }
  function removeItem(idx) {
    const items = (doc.items || []).filter((_, i) => i !== idx);
    setDoc({ ...doc, items });
  }
  function moveItem(idx, dir) {
    const items = [...(doc.items || [])];
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    [items[idx], items[j]] = [items[j], items[idx]];
    setDoc({ ...doc, items });
  }

  async function save() {
    await api.put(`/admin/content-blocks/${doc.section_key}`, {
      section_key: doc.section_key,
      title: doc.title || "",
      subtitle: doc.subtitle || "",
      items: doc.items || [],
      i18n: doc.i18n || {},
      enabled: !!doc.enabled,
      order: doc.order ?? 0,
    });
    toast.success("Block saved");
    load();
  }

  async function del(key) {
    if (!confirm(`Delete content block "${key}"? This removes it from the public site.`)) return;
    await api.delete(`/admin/content-blocks/${key}`);
    toast.success("Deleted");
    setActiveKey(null); setDoc(null);
    load();
  }

  async function toggleEnabled() {
    if (!doc) return;
    const next = { ...doc, enabled: !doc.enabled };
    setDoc(next);
    await api.put(`/admin/content-blocks/${doc.section_key}`, {
      section_key: doc.section_key,
      title: doc.title || "", subtitle: doc.subtitle || "",
      items: doc.items || [], i18n: doc.i18n || {},
      enabled: !doc.enabled, order: doc.order ?? 0,
    });
    toast.success(next.enabled ? "Visible on site" : "Hidden from site");
    load();
  }

  return (
    <StaffLayout>
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl">Content blocks</h1>
          <p className="text-sm text-[#71717A] mt-1">FAQs, Q&A, surroundings, fine print, support sections — anything you want on the site. All multilingual, all removable.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="cb-add" className="bg-brand-primary text-white px-5 py-2.5 rounded-sm text-sm flex items-center gap-2">
          <Plus size={14} /> New block
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="bg-white border border-[#E4E4E7] rounded-md p-3 lg:max-h-[calc(100vh-220px)] lg:overflow-y-auto">
          {list.map((b) => (
            <div key={b.section_key} className="flex items-center gap-1">
              <button
                onClick={() => pick(b.section_key)}
                data-testid={`cb-pick-${b.section_key}`}
                className={`flex-1 text-left px-3 py-2 rounded-sm text-sm truncate flex items-center gap-2 ${activeKey === b.section_key ? "bg-brand-secondary text-brand-primary" : "hover:bg-[#FAFAFA]"}`}
              >
                {b.enabled ? <Eye size={12} className="text-emerald-600" /> : <EyeOff size={12} className="text-[#A1A1AA]" />}
                <span className="truncate">{b.title}</span>
              </button>
              <button data-testid={`cb-del-${b.section_key}`} onClick={() => del(b.section_key)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-sm"><Trash2 size={13} /></button>
            </div>
          ))}
          {list.length === 0 && <div className="text-sm text-[#71717A] p-3">No content blocks. Add one →</div>}
        </aside>

        <div className="lg:col-span-3 bg-white border border-[#E4E4E7] rounded-md">
          {doc ? (
            <>
              <div className="flex items-center gap-2 border-b px-6 py-3 flex-wrap">
                <Globe size={14} className="text-brand-muted" />
                {LANGS.map((l) => (
                  <button key={l.code} data-testid={`cb-lang-${l.code}`} onClick={() => setLang(l.code)}
                    className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm ${lang === l.code ? "bg-brand-primary text-white" : "text-[#71717A] hover:bg-[#FAFAFA]"}`}>{l.flag} {l.code}</button>
                ))}
                <button data-testid="cb-toggle-enabled" onClick={toggleEnabled} className={`ml-auto text-xs px-3 py-1.5 rounded-sm ${doc.enabled ? "bg-emerald-50 text-emerald-700" : "bg-[#FAFAFA] text-[#71717A]"}`}>
                  {doc.enabled ? "Visible on site" : "Hidden — click to publish"}
                </button>
              </div>
              <div className="p-6 space-y-5">
                <div className="grid sm:grid-cols-3 gap-4">
                  <label className="block sm:col-span-2">
                    <span className="text-xs uppercase tracking-wider text-[#71717A]">Section title ({lang})</span>
                    <input data-testid="cb-title" value={getSectionField("title")} onChange={(e) => setSectionField("title", e.target.value)} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
                  </label>
                  <label className="block">
                    <span className="text-xs uppercase tracking-wider text-[#71717A]">Order</span>
                    <input type="number" data-testid="cb-order" value={doc.order ?? 0} onChange={(e) => setDoc({ ...doc, order: parseInt(e.target.value || 0, 10) })} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
                  </label>
                </div>

                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-[#71717A]">Subtitle ({lang})</span>
                  <input data-testid="cb-subtitle" value={getSectionField("subtitle")} onChange={(e) => setSectionField("subtitle", e.target.value)} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
                </label>

                <div className="text-xs uppercase tracking-wider text-[#71717A] flex justify-between items-center pt-2">
                  <span>Items / Q&A</span>
                  <button onClick={addItem} data-testid="cb-add-item" className="text-brand-primary flex items-center gap-1"><Plus size={12} /> Add item</button>
                </div>

                <div className="space-y-3">
                  {(doc.items || []).map((it, i) => (
                    <div key={i} data-testid={`cb-item-${i}`} className="border border-[#E4E4E7] rounded-md p-4">
                      <div className="flex items-center gap-2 mb-3 text-xs text-[#71717A]">
                        <GripVertical size={14} />
                        <span>Item {i + 1}</span>
                        <button onClick={() => moveItem(i, -1)} className="ml-auto hover:text-brand-text">↑</button>
                        <button onClick={() => moveItem(i, +1)} className="hover:text-brand-text">↓</button>
                        <button onClick={() => removeItem(i)} className="text-rose-600 hover:bg-rose-50 p-1 rounded"><Trash2 size={13} /></button>
                      </div>
                      <label className="block mb-2">
                        <span className="text-xs text-[#71717A]">Title / Question ({lang})</span>
                        <input data-testid={`cb-item-title-${i}`} value={getItemField(i, "title")} onChange={(e) => setItemField(i, "title", e.target.value)} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
                      </label>
                      <label className="block">
                        <span className="text-xs text-[#71717A]">Content / Answer ({lang})</span>
                        <textarea data-testid={`cb-item-content-${i}`} rows={3} value={getItemField(i, "content")} onChange={(e) => setItemField(i, "content", e.target.value)} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
                      </label>
                    </div>
                  ))}
                  {(doc.items || []).length === 0 && (
                    <div className="text-sm text-[#71717A] border border-dashed border-[#E4E4E7] rounded-md p-6 text-center">No items yet — click "Add item".</div>
                  )}
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-[#E4E4E7]">
                  <a href={`/info/${doc.section_key}`} target="_blank" rel="noreferrer" className="text-xs text-brand-primary">→ View public page</a>
                  <button data-testid="cb-save" onClick={save} className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm">Save block</button>
                </div>
              </div>
            </>
          ) : (
            <div className="p-12 text-center text-[#71717A]">Select or add a content block.</div>
          )}
        </div>
      </div>

      {showAdd && <AddBlockModal onClose={() => setShowAdd(false)} onCreated={(key) => { setShowAdd(false); load(); setTimeout(() => pick(key), 200); }} />}
    </StaffLayout>
  );
}

function AddBlockModal({ onClose, onCreated }) {
  const [f, setF] = useState({ section_key: "", title: "", subtitle: "" });
  async function submit(e) {
    e.preventDefault();
    const key = f.section_key.toLowerCase().replace(/[^a-z0-9_-]+/g, "_").replace(/^_|_$/g, "");
    if (!key) { toast.error("Invalid key"); return; }
    try {
      await api.post("/admin/content-blocks", { section_key: key, title: f.title, subtitle: f.subtitle, items: [], enabled: true, order: 99 });
      toast.success("Block created");
      onCreated(key);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white w-full max-w-md rounded-md">
        <div className="p-5 border-b flex justify-between items-center">
          <h2 className="font-display text-2xl">New content block</h2>
          <button type="button" onClick={onClose} className="p-2"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#71717A]">URL key</span>
            <input data-testid="new-cb-key" required value={f.section_key} onChange={(e) => setF({ ...f, section_key: e.target.value })} placeholder="e.g. cleaning-policy" className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
            <span className="text-[10px] text-[#71717A]">Will be /info/{f.section_key || "your-key"}</span>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#71717A]">Title (English)</span>
            <input data-testid="new-cb-title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#71717A]">Subtitle (English, optional)</span>
            <input data-testid="new-cb-subtitle" value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
          </label>
        </div>
        <div className="p-5 border-t flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm">Cancel</button>
          <button data-testid="new-cb-save" type="submit" className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm">Create</button>
        </div>
      </form>
    </div>
  );
}
